import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress, Switch } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

import moment from "moment";
import { useGetSettingByKeyQuery, useUpsertSettingMutation } from "../../../../reducers/settingsApi";
import { useDispatch } from "react-redux";
import { updateIPAlertStatus, updateIPFlagStatus } from "../../../../actions/user";
import { toast } from "react-toastify";

const IPDetailsView = ({ trafficData, onViewHistory, onViewAlerts, onEmailClick, onUpdate }) => {
    const settingKey = "app.alertip";
    const { data: settingRes } = useGetSettingByKeyQuery(settingKey);
    const [upsertSetting, { isLoading: isSaving }] = useUpsertSettingMutation();

    const dispatch = useDispatch();
    const [alertSettings, setAlertSettings] = useState({
        pageViews: 20,
        visits: 2,
        checkoutVisits: 4,
    });
    const [isIgnored, setIsIgnored] = useState(trafficData?.isAlertIgnored || false);
    const [isFlagged, setIsFlagged] = useState(trafficData?.flagged || false);
    const [isSundial, setIsSundial] = useState(trafficData?.sundialFlag || false);
    const [isGoogleCrawl, setIsGoogleCrawl] = useState(trafficData?.googleCrawlFlag || false);
    const [isRestrictedFlag, setIsRestrictedFlag] = useState(trafficData?.isRestricted || false);
    const [isBlacklistedFlag, setIsBlacklistedFlag] = useState(trafficData?.blacklistIp || false);

    // Sync state if trafficData updates from props
    useEffect(() => {
        setIsFlagged(trafficData?.flagged || false);
        setIsSundial(trafficData?.sundialFlag || false);
        setIsGoogleCrawl(trafficData?.googleCrawlFlag || false);
        setIsRestrictedFlag(trafficData?.isRestricted || false);
        setIsBlacklistedFlag(trafficData?.blacklistIp || false);
    }, [trafficData]);

    useEffect(() => {
        if (settingRes?.data?.value) {
            setAlertSettings(settingRes.data.value);
        }
    }, [settingRes]);

    const handleUpdate = (field, delta) => {
        setAlertSettings(prev => ({
            ...prev,
            [field]: Math.max(0, (prev[field] || 0) + delta)
        }));
    };

    const handleSave = async () => {
        try {
            const payload = {
                key: settingKey,
                value: alertSettings,
                description: "Alert conditions for IP traffic",
                category: "app",
                isPublic: true
            };
            await upsertSetting(payload).unwrap();
            toast.success("Alert conditions saved successfully");
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Failed to save alert conditions:", error);
            toast.error("Failed to save alert conditions");
        }
    };

    const handleToggleIgnore = async (value) => {
        try {
            setIsIgnored(value);
            const res = await dispatch(updateIPAlertStatus(trafficData?.ip, value));
            if (res?.success) {
                toast.success(`Alerts ${value ? 'ignored' : 'enabled'} for this IP`);
                if (onUpdate) onUpdate();
            }
        } catch (error) {
            console.error("Failed to update alert status:", error);
            toast.error("Failed to update alert status");
            setIsIgnored(!value); // Revert on failure
        }
    };

    const { data: restrictionRes } = useGetSettingByKeyQuery("app.setting.mapIp");
    const { data: blacklistRes } = useGetSettingByKeyQuery("app.setting.backlistIp");
    const restrictedIps = Array.isArray(restrictionRes?.data?.value) ? restrictionRes.data.value : (restrictionRes?.data?.value ? [restrictionRes.data.value] : []);
    const blacklistedIps = Array.isArray(blacklistRes?.data?.value) ? blacklistRes.data.value : (blacklistRes?.data?.value ? [blacklistRes.data.value] : []);

    const handleToggleFlag = async (field, checked) => {
        if (field === 'flagged') setIsFlagged(checked);
        if (field === 'sundialFlag') setIsSundial(checked);
        if (field === 'googleCrawlFlag') setIsGoogleCrawl(checked);
        if (field === 'isRestricted') setIsRestrictedFlag(checked);
        if (field === 'blacklistIp') setIsBlacklistedFlag(checked);

        try {
            const payload = {
                flagged: field === 'flagged' ? checked : isFlagged,
                sundialFlag: field === 'sundialFlag' ? checked : isSundial,
                googleCrawlFlag: field === 'googleCrawlFlag' ? checked : isGoogleCrawl,
                isRestricted: field === 'isRestricted' ? checked : isRestrictedFlag,
                blacklistIp: field === 'blacklistIp' ? checked : isBlacklistedFlag,
                [field]: checked
            };
            const res = await dispatch(updateIPFlagStatus(trafficData?.ip, payload));
            if (res?.success) {
                toast.success(`${field === 'blacklistIp' ? 'Blacklist' : field} marked as ${checked ? 'true' : 'false'}`);
            }

            if (field === 'isRestricted') {
                let updatedIps = [...restrictedIps];
                if (checked && !updatedIps.includes(trafficData?.ip)) {
                    updatedIps.push(trafficData?.ip);
                } else if (!checked) {
                    updatedIps = updatedIps.filter(ip => ip !== trafficData?.ip);
                }

                await upsertSetting({
                    key: "app.setting.mapIp",
                    value: updatedIps,
                    description: "List of map Ips",
                    category: "app",
                    isPublic: true
                }).unwrap();
            } else if (field === 'blacklistIp') {
                let updatedIps = [...blacklistedIps];
                if (checked && !updatedIps.includes(trafficData?.ip)) {
                    updatedIps.push(trafficData?.ip);
                } else if (!checked) {
                    updatedIps = updatedIps.filter(ip => ip !== trafficData?.ip);
                }

                await upsertSetting({
                    key: "app.setting.backlistIp",
                    value: updatedIps,
                    description: "List of map Ips",
                    category: "app",
                    isPublic: true
                }).unwrap();
            }
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Failed to update status:", error);
            toast.error("Failed to update status");
            if (field === 'flagged') setIsFlagged(!checked);
            if (field === 'sundialFlag') setIsSundial(!checked);
            if (field === 'googleCrawlFlag') setIsGoogleCrawl(!checked);
            if (field === 'isRestricted') setIsRestrictedFlag(!checked);
            if (field === 'blacklistIp') setIsBlacklistedFlag(!checked);
        }
    };

    const isRestrictedText = restrictedIps.includes(trafficData?.ip) || isRestrictedFlag;

    const renderAccountValue = () => {
        if (trafficData?.emails?.length > 0) {
            return trafficData.emails.map((email, i) => (
                <span key={i}>
                    <span
                        onClick={() => onEmailClick && onEmailClick(email)}
                        style={{ cursor: "pointer", textDecoration: "underline", color: "#007bff" }}
                    >
                        {email}
                    </span>
                    {i < trafficData.emails.length - 1 ? ", " : ""}
                </span>
            ));
        } else if (trafficData?.email) {
            return (
                <span
                    onClick={() => onEmailClick && onEmailClick(trafficData.email)}
                    style={{ cursor: "pointer", textDecoration: "underline", color: "#007bff" }}
                >
                    {trafficData.email}
                </span>
            );
        }
        return "-";
    };

    const detailRows = [
        { label: "Page Views", value: trafficData?.visitCount || 0, isLink: true, onClick: onViewHistory, showAlert: true },
        { label: "Visits (days)", value: trafficData?.visitDays || 0, showAlert: true },
        { label: "Checkout Visits", value: trafficData?.checkoutVisits || 0, showAlert: true }, // Duplicate as per image
        { label: "Transactions", value: trafficData?.transactionCount || 0 },
        { label: "Account", value: renderAccountValue(), isLink: false, color: "#1D1D1B" },
        {
            label: "IP Restriction", value: (
                <div className="d-flex align-items-center">
                    <span style={{ marginRight: "10px", color: isRestrictedText ? '#FF1F1F' : '#007bff' }}>
                        {isRestrictedText ? "Restricted" : "Unrestricted"}
                    </span>
                    <Switch
                        checked={isRestrictedFlag}
                        onChange={(e) => handleToggleFlag('isRestricted', e.target.checked)}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#1D1D1B' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1D1D1B' },
                            marginRight: '-10px'
                        }}
                        size="small"
                    />
                </div>
            ), isLink: false
        },
        {
            label: "Blacklist Status", value: (
                <div className="d-flex align-items-center">
                    {blacklistedIps.includes(trafficData?.ip) || isBlacklistedFlag ? (
                        <button
                            onClick={() => handleToggleFlag('blacklistIp', false)}
                            className="sofiaBold px-2 py-1"
                            style={{ backgroundColor: "#FF1F1F", color: "#FFF", border: "none", borderRadius: "8px", fontSize: "12px", width: "90px", transition: "all 0.2s" }}
                        >
                            Unblock IP
                        </button>
                    ) : (
                        <button
                            onClick={() => handleToggleFlag('blacklistIp', true)}
                            className="sofiaBold px-2 py-1"
                            style={{ backgroundColor: "#1D1D1B", color: "#FFF", border: "none", borderRadius: "8px", fontSize: "12px", width: "90px", transition: "all 0.2s" }}
                        >
                            Blacklist IP
                        </button>
                    )}
                </div>
            ), isLink: false
        },
        {
            label: "Flagged", value: (
                <Switch
                    checked={isFlagged}
                    onChange={(e) => handleToggleFlag('flagged', e.target.checked)}
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF1F1F' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF1F1F' },
                        marginRight: '-10px'
                    }}
                    size="small"
                />
            ), isLink: false
        },
        {
            label: "Sundial Flag", value: (
                <Switch
                    checked={isSundial}
                    onChange={(e) => handleToggleFlag('sundialFlag', e.target.checked)}
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#007bff' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#007bff' },
                        marginRight: '-10px'
                    }}
                    size="small"
                />
            ), isLink: false
        },
        {
            label: "Google Crawl Flag", value: (
                <Switch
                    checked={isGoogleCrawl}
                    onChange={(e) => handleToggleFlag('googleCrawlFlag', e.target.checked)}
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#28a745' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#28a745' },
                        marginRight: '-10px'
                    }}
                    size="small"
                />
            ), isLink: false
        },
        { label: "First Seen", value: trafficData?.firstVisitAt ? moment(trafficData.firstVisitAt).format("MM/DD/YYYY") : "-" },
        { label: "Last Seen", value: trafficData?.lastVisitAt ? moment(trafficData.lastVisitAt).format("MM/DD/YYYY") : "-" },
    ];

    return (
        <Box sx={{ pb: 4 }}>
            <Box className="bg-white m-3" style={{ borderRadius: "10px", border: "1px solid #EEEEEE" }}>
                {detailRows.map((row, idx) => (
                    <Box
                        key={idx}
                        className="d-flex justify-content-between align-items-center py-3 px-3"
                        style={{ borderBottom: idx === detailRows.length - 1 ? "none" : "1px solid #EEEEEE" }}
                    >
                        <Typography className="sofiaBold" style={{ fontSize: "14px", color: "#1D1D1B" }}>{row.label}</Typography>
                        <Box className="d-flex align-items-center gap-2">
                            <Box
                                className="sofiaBold"
                                onClick={row.onClick}
                                style={{
                                    fontSize: "14px",
                                    color: row.color || (row.isLink ? "#007bff" : "#1D1D1B"),
                                    cursor: row.isLink ? "pointer" : "default",
                                    textDecoration: row.isLink ? "underline" : "none",
                                    width: "min-content",
                                    textAlign: "right"
                                }}
                            >
                                {row.value}
                            </Box>
                            {row.showAlert && (
                                <ErrorOutlineIcon
                                    onClick={() => trafficData?.alertSummary?.active > 0 && onViewAlerts?.()}
                                    style={{
                                        color: "#FFD700",
                                        fontSize: "18px",
                                        cursor: trafficData?.alertSummary?.active > 0 ? "pointer" : "default"
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box className="bg-white m-3" style={{ borderRadius: "10px", border: "1px solid #EEEEEE" }}>
                <Box className="d-flex justify-content-between align-items-center py-3 px-3" style={{ borderBottom: "1px solid #EEEEEE" }}>
                    <Typography className="sofiaRegular" style={{ fontSize: "14px", color: "#666" }}>Alert Conditions</Typography>
                    <button
                        disabled={isSaving}
                        onClick={handleSave}
                        className="sofiaBold px-3 py-1 bg-white"
                        style={{ border: "1px solid #1D1D1B", borderRadius: "8px", fontSize: "13px", cursor: isSaving ? "not-allowed" : "pointer" }}
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </button>
                </Box>

                {[
                    { label: "Page Views", val: alertSettings.pageViews, field: "pageViews" },
                    { label: "Visits (days)", val: alertSettings.visits, field: "visits" },
                    { label: "Checkout Visits", val: alertSettings.checkoutVisits, field: "checkoutVisits" }
                ].map((item, idx) => (
                    <Box key={idx} className="d-flex justify-content-between align-items-center py-3 px-3" style={{ borderBottom: "1px solid #EEEEEE" }}>
                        <Typography className="sofiaBold" style={{ fontSize: "14px", color: "#1D1D1B" }}>{item.label}</Typography>
                        <Box className="d-flex align-items-center gap-3">
                            <Box
                                onClick={() => handleUpdate(item.field, -1)}
                                className="d-flex justify-content-center align-items-center"
                                style={{ border: "1px solid #BBBBBB", borderRadius: "4px", cursor: "pointer", width: "24px", height: "24px" }}
                            >
                                <RemoveIcon sx={{ fontSize: "16px", color: "#BBBBBB" }} />
                            </Box>
                            <Typography className="sofiaBold" style={{ fontSize: "14px", width: "20px", textAlign: "center" }}>{item.val}</Typography>
                            <Box
                                onClick={() => handleUpdate(item.field, 1)}
                                className="d-flex justify-content-center align-items-center"
                                style={{ border: "1px solid #BBBBBB", borderRadius: "4px", cursor: "pointer", width: "24px", height: "24px" }}
                            >
                                <AddIcon sx={{ fontSize: "16px", color: "#BBBBBB" }} />
                            </Box>
                        </Box>
                    </Box>
                ))}

                <Box className="d-flex justify-content-between align-items-center py-3 px-3">
                    <Typography className="sofiaBold" style={{ fontSize: "14px", color: "#1D1D1B" }}>Ignore Alert If Transaction?</Typography>
                    <Box className="d-flex" style={{ backgroundColor: "#F5F5F5", borderRadius: "5px", padding: "2px" }}>
                        <button
                            onClick={() => handleToggleIgnore(false)}
                            className="sofiaBold px-3 py-1"
                            style={{
                                border: "none",
                                borderRadius: !isIgnored ? "4px" : "none",
                                backgroundColor: !isIgnored ? "#FFF" : "transparent",
                                color: !isIgnored ? "#1D1D1B" : "#BBBBBB",
                                fontSize: "12px",
                                boxShadow: !isIgnored ? "0px 1px 3px rgba(0,0,0,0.1)" : "none"
                            }}
                        >
                            No
                        </button>
                        <button
                            onClick={() => handleToggleIgnore(true)}
                            className="sofiaBold px-3 py-1"
                            style={{
                                border: "none",
                                borderRadius: isIgnored ? "4px" : "none",
                                backgroundColor: isIgnored ? "#FFF" : "transparent",
                                color: isIgnored ? "#1D1D1B" : "#BBBBBB",
                                fontSize: "12px",
                                boxShadow: isIgnored ? "0px 1px 3px rgba(0,0,0,0.1)" : "none"
                            }}
                        >
                            Yes
                        </button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default IPDetailsView;
